import type { IConfiguration } from './types';

/**
 * Names of the individual attach types exposed by the modal picker.
 *
 * `LOCATION` is reserved for a future feature (the `AttachLoc` component is
 * currently commented out in the library), so it has no dedicated
 * configurator UI yet, but it is kept here so the shape stays stable when it
 * ships.
 */
export enum AttachUiType {
	IMAGE = 'image',
	PHOTO = 'photo',
	DOCUMENT = 'document',
	LOCATION = 'location',
}

/**
 * UI-only configuration for the attach feature.
 *
 * This flag only controls whether the attach affordances (button, drag and
 * drop, paste, and individual picker entries) are rendered in the widget. It
 * is independent from `file_upload`, which is the backend flag governing
 * whether file upload is actually allowed and can be overwritten server-side.
 *
 * @remarks
 * This UI flag is authoritative for hiding the attach UI: `file_upload`
 * never overrides it. If this flag hides the attach feature (or a specific
 * type), it stays hidden regardless of the current `file_upload.enabled`
 * value — including when the backend flag is (or becomes) `true`.
 *
 * - `false` — hides the attach feature entirely (button, drag and drop,
 *   paste, and every entry in the picker modal).
 * - An object — hides only the listed attach types in the picker modal
 *   (e.g. `{ image: false }` hides the "Image" entry only). Omitted keys
 *   default to visible (`true` is implicit and never needs to be set
 *   explicitly).
 */
export type IAttachUiConfiguration =
	| false
	| Partial<Record<AttachUiType, false>>;

/**
 * Whether the attach feature (button, drag and drop, paste) should be
 * rendered at all, based on the UI-only `attach_ui` flag.
 *
 * Returns `false` when `attach_ui === false`, and also when every
 * individually-toggleable attach type (see {@link ATTACH_UI_RENDERED_TYPES})
 * has been disabled one by one — in that case there is nothing left for the
 * button to open, so it must not be rendered either.
 *
 * Does not take `file_upload.enabled` into account — callers must combine
 * this with the backend flag themselves, since the two are independent
 * gates (UI visibility vs. actual upload capability).
 *
 * @param configuration - Configuration slice containing `attach_ui`.
 */
export const isAttachUiVisible = (
	configuration: Pick<IConfiguration, 'attach_ui'>,
): boolean => {
	if (configuration.attach_ui === false) return false;
	return ATTACH_UI_RENDERED_TYPES.some((type) =>
		isAttachTypeUiVisible(configuration, type),
	);
};

/**
 * Whether a specific attach type (e.g. "image") should be rendered in the
 * picker modal, based on the UI-only `attach_ui` flag.
 *
 * Returns `false` whenever the whole attach feature is hidden
 * (`attach_ui === false`), or when the given `type` key is explicitly set to
 * `false` in the `attach_ui` object. Any other case (including `attach_ui`
 * being `undefined`) defaults to visible.
 *
 * @param configuration - Configuration slice containing `attach_ui`.
 * @param type - The attach type to check (see {@link AttachUiType}).
 */
export const isAttachTypeUiVisible = (
	configuration: Pick<IConfiguration, 'attach_ui'>,
	type: AttachUiType,
): boolean => {
	const { attach_ui } = configuration;
	if (attach_ui === false) return false;
	return attach_ui?.[type] !== false;
};

/**
 * Attach types that actually render a picker entry in the library today.
 *
 * `location` is intentionally excluded: its picker entry (`AttachLoc`) is
 * currently commented out, so it never contributes a visible button and
 * must not be taken into account when deciding whether the whole attach
 * button has anything left to show (see {@link isAttachUiVisible}).
 */
const ATTACH_UI_RENDERED_TYPES: ReadonlyArray<
	Exclude<AttachUiType, AttachUiType.LOCATION>
> = [AttachUiType.IMAGE, AttachUiType.PHOTO, AttachUiType.DOCUMENT];
